import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Search, Filter, AlertTriangle, Calendar, Clock, XCircle, RefreshCw, Eye, X, List, LayoutGrid, UserCheck, Zap, FileSpreadsheet, BarChart3, ArrowUpRight, ArrowDownRight, Minus, CalendarDays, Car, Database } from "lucide-react";
import { format, startOfWeek, startOfMonth, endOfMonth, addDays } from "date-fns";
import { es } from "date-fns/locale";
import CalendarView from "./CalendarView";
import AdvisorView from "./AdvisorView";
import AnalyticsView from "./AnalyticsView";
import DetailEditForm from "@/components/DetailEditForm";
import usePersistedState, { clearPersistedFiltersByPrefix } from "@/hooks/usePersistedState";
import { buildYearOptions } from "@/lib/appointmentYears";
import axios from "axios";
import QuickReception from "@/components/QuickReception";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pendiente_confirmacion: { label: "Pte. Confirmacion", class: "bg-amber-100 text-amber-800 border-amber-200" },
  confirmada: { label: "Confirmada", class: "bg-blue-100 text-blue-800 border-blue-200" },
  pendiente_reprogramacion: { label: "Pte. Reprogramacion", class: "bg-purple-100 text-purple-800 border-purple-200" },
  cancelada_cliente: { label: "Cancelada (cliente)", class: "bg-red-100 text-red-800 border-red-200" },
  cancelada_taller: { label: "Cancelada (taller)", class: "bg-red-100 text-red-800 border-red-200" },
  pendiente: { label: "Pendiente", class: "bg-amber-100 text-amber-700 border-amber-200" },
};

// Iter 210 — Helper TZ Madrid: devuelve "YYYY-MM-DD HH:mm" en Europe/Madrid.
// Comparable como string con `${apt.date} ${apt.time_slot}` para clasificar
// citas confirmadas en "próximas" vs "pasadas" sin tocar el status real en BD.
const NOW_MADRID_FMT = new Intl.DateTimeFormat("sv-SE", {
  timeZone: "Europe/Madrid",
  year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", hour12: false,
});
const nowInMadrid = () => NOW_MADRID_FMT.format(new Date()).replace("T", " ");
const aptDateTimeString = (a) => `${a.date || "9999-12-31"} ${(a.time_slot || "00:00").slice(0, 5)}`;
// Una cita está "pasada" cuando su fecha+hora confirmada (en Madrid) < ahora.
// Si está cancelada, no se clasifica como "pasada" (es otro estado terminal).
const isPastConfirmed = (a) => a?.status === "confirmada" && aptDateTimeString(a) < nowInMadrid();
const isUpcomingConfirmed = (a) => a?.status === "confirmada" && aptDateTimeString(a) >= nowInMadrid();

const ADMIN_STATUSES = [
  { value: "pendiente_confirmacion", label: "Pte. Confirmacion" },
  { value: "confirmada", label: "Confirmada" },
  { value: "pendiente_reprogramacion", label: "Pte. Reprogramacion" },
  { value: "cancelada_cliente", label: "Cancelada (cliente)" },
  { value: "cancelada_taller", label: "Cancelada (taller)" },
];


export default function AppointmentsPage() {
  const { axiosAuth } = useAuth();
  const [appointments, setAppointments] = useState([]);
  // Iter 225 — fuente de verdad SERVIDOR para "confirmadas pasadas".
  // Evita depender del array `appointments` truncado por limit del listado.
  const [pastConfirmedServer, setPastConfirmedServer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [advisors, setAdvisors] = useState([]);
  const [activeView, setActiveView] = usePersistedState("appointments.activeView", "calendar"); // "list" | "calendar" | "advisor" | "analytics"
  const [showDialog, setShowDialog] = useState(false);
  const [detailApt, setDetailApt] = useState(null);
  const [search, setSearch] = usePersistedState("appointments.search", "");
  const [statusFilter, setStatusFilter] = usePersistedState("appointments.statusFilter", "todos");
  // Iter 233 (P1) — Filtro de asesor: "todos" | "unassigned" | <advisor_id>
  const [advisorFilter, setAdvisorFilter] = usePersistedState("appointments.advisorFilter", "todos");
  // Iter 210: sub-filtro temporal para "confirmada" — separa próximas vs pasadas
  // Valores: "proximas" (default) | "pasadas" | "todas"
  const [confirmadaTemporal, setConfirmadaTemporal] = usePersistedState("appointments.confirmadaTemporal", "proximas");
  // Iter 128: ordenación de la vista Lista — default dinámico según filtro
  const [sortOption, setSortOption] = usePersistedState("appointments.sortOption", "appt_date_asc");
  const sortTouchedRef = useRef(false);  // true cuando el usuario elige manualmente
  // Al cambiar a "requieren gestión" sin tocar el sort, ordenar por solicitud más antigua
  useEffect(() => {
    if (sortTouchedRef.current) return;
    if (statusFilter === "requiere_atencion" || statusFilter === "pendiente_confirmacion") {
      setSortOption("requested_oldest");
    } else {
      setSortOption("appt_date_asc");
    }
  }, [statusFilter]);
  const [editingId, setEditingId] = useState(null);
  const [showQuick, setShowQuick] = useState(false);
  // Iter 217 — Default cambiado a 'next_30d' (agenda futura). Si el usuario ya
  // tiene preferencia guardada en localStorage (ej. 'month' desde Iter 210), se
  // respeta hasta que cambie manualmente.
  const [period, setPeriod] = usePersistedState("appointments.period", "next_30d");
  const [selectedYear, setSelectedYear] = usePersistedState("appointments.selectedYear", new Date().getFullYear());
  const [yearOptions, setYearOptions] = useState(() => buildYearOptions());

  // Auto-refresh (Iter 127) — silenciosa, pausa con modales abiertos
  const REFRESH_INTERVAL_MS = 30000;
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const [refreshError, setRefreshError] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0); // re-render del indicador "hace X seg"
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const wasModalOpenRef = useRef(false);

  // Carga el rango real de años (con citas en BD) y completa el selector.
  useEffect(() => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    axios.get(`${API}/appointments/year-range`)
      .then(r => {
        const ys = Array.isArray(r.data?.years) && r.data.years.length > 0
          ? r.data.years
          : buildYearOptions();
        setYearOptions(ys);
      })
      .catch(() => setYearOptions(buildYearOptions()));
  }, []);
  const [comparisonData, setComparisonData] = useState(null);
  const [form, setForm] = useState({
    client_name: "", client_email: "", client_phone: "",
    vehicle_info: "", service_type: "", date: "", time_slot: "", notes: "", assigned_employee: ""
  });

  // Iter 217 / 225 — Periodo y rango personalizado. Se declaran ARRIBA para
  // evitar TDZ con `fetchPastConfirmed` (que los referencia más abajo).
  const [customFrom, setCustomFrom] = usePersistedState("appointments.customFrom", "");
  const [customTo, setCustomTo] = usePersistedState("appointments.customTo", "");

  const fetchAppointments = useCallback(async ({ silent = false } = {}) => {
    try {
      // Iter 220 — `limit=10000` para evitar truncado de confirmadas pasadas
      // en instalaciones con histórico extenso (default backend = 5000).
      const res = await axiosAuth().get("/appointments?limit=10000");
      setAppointments(res.data);
      setLastUpdated(Date.now());
      setRefreshError(false);
    } catch {
      if (silent) {
        // Aviso discreto, sin toast modal — el indicador del header mostrará el error
        setRefreshError(true);
      } else {
        toast.error("Error al cargar citas");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [axiosAuth]);

  const fetchComparison = useCallback(() => {
    axiosAuth().get(`/analytics/charts?period=${period}`)
      .then(r => setComparisonData(r.data))
      .catch(() => {});
  }, [axiosAuth, period]);

  // Iter 225 (P1) — Fetch dedicado de "Confirmadas pasadas" desde backend.
  // No depende del array `appointments` truncado: el endpoint /api/appointments/past-confirmed
  // aplica el scope per-periodo en servidor y devuelve la lista COMPLETA.
  const fetchPastConfirmed = useCallback(async () => {
    try {
      const params = new URLSearchParams({ period });
      if (period === "custom") {
        if (customFrom) params.set("date_from", customFrom);
        if (customTo) params.set("date_to", customTo);
      }
      const res = await axiosAuth().get(`/appointments/past-confirmed?${params.toString()}`);
      setPastConfirmedServer(Array.isArray(res.data) ? res.data : []);
    } catch {
      // Silencioso: el indicador del header reflejará un fallo si lo hay
      setPastConfirmedServer([]);
    }
  }, [axiosAuth, period, customFrom, customTo]);

  const manualRefresh = useCallback(async () => {
    setIsManualRefreshing(true);
    try {
      await fetchAppointments({ silent: true });
      fetchComparison();
      fetchPastConfirmed();
    } finally {
      setIsManualRefreshing(false);
    }
  }, [fetchAppointments, fetchComparison, fetchPastConfirmed]);

  useEffect(() => {
    fetchAppointments();
    axiosAuth().get("/config").then(res => setConfig(res.data)).catch(() => {});
    axiosAuth().get("/advisors").then(res => setAdvisors(res.data)).catch(() => {});
  }, [fetchAppointments]);

  // Iter 225 — refetch past-confirmed cuando cambia el periodo.
  useEffect(() => {
    fetchPastConfirmed();
  }, [fetchPastConfirmed]);

  // Deep-link: ?id=<appointment_id> abre el detalle automáticamente (Iter 163)
  // Iter 215 — ?statusFilter=<key> y ?center=<center> aplican filtros al entrar
  // desde el Dashboard (Inteligencia de negocio). Se consumen una sola vez.
  const deepLinkConsumedRef = useRef(false);
  useEffect(() => {
    if (deepLinkConsumedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const sf = params.get("statusFilter");
    if (sf) {
      setStatusFilter(sf);
      // Si entra por confirmadas_proximas/pasadas, el sub-toggle se ajusta a tono.
      if (sf === "confirmada_proximas") setConfirmadaTemporal("proximas");
      else if (sf === "confirmada_pasadas") setConfirmadaTemporal("pasadas");
      deepLinkConsumedRef.current = true;
    }
    const aptId = params.get("id");
    if (!aptId || !appointments?.length) return;
    const found = appointments.find(a => a.id === aptId);
    if (found) {
      setDetailApt(found);
      deepLinkConsumedRef.current = true;
    }
  }, [appointments, setStatusFilter, setConfirmadaTemporal]);

  // Fetch comparison data when period changes
  useEffect(() => {
    fetchComparison();
  }, [fetchComparison]);

  // ── AUTO-REFRESH (Iter 127) ─────────────────────────────────────────────
  // Cada REFRESH_INTERVAL_MS refresca silenciosamente la lista + KPIs.
  // Pausa cuando hay modal abierto, cuando la pestaña está oculta, o cuando
  // hay un refresh manual en curso. Al cerrar el modal hace refresh inmediato.
  const isModalOpen = showDialog || showQuick || !!detailApt;

  useEffect(() => {
    // Refresh inmediato al cerrar cualquier modal
    if (wasModalOpenRef.current && !isModalOpen) {
      fetchAppointments({ silent: true });
      fetchComparison();
    }
    wasModalOpenRef.current = isModalOpen;
  }, [isModalOpen, fetchAppointments, fetchComparison]);

  useEffect(() => {
    const interval = setInterval(() => {
      // Pausa si hay modal o si la pestaña está en background
      if (isModalOpen) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      fetchAppointments({ silent: true });
      fetchComparison();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isModalOpen, fetchAppointments, fetchComparison]);

  // Ticker para refrescar el texto "hace X segundos" cada 5s sin pegar a la API
  useEffect(() => {
    const t = setInterval(() => setRefreshTick(v => v + 1), 5000);
    return () => clearInterval(t);
  }, []);

  // Texto humano del último refresh
  const lastUpdatedLabel = useMemo(() => {
    void refreshTick; // dependencia para forzar re-render
    const seconds = Math.max(0, Math.floor((Date.now() - lastUpdated) / 1000));
    if (seconds < 5) return "hace unos segundos";
    if (seconds < 60) return `hace ${seconds} seg`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `hace ${minutes} min`;
    return `hace ${Math.floor(minutes / 60)} h`;
  }, [lastUpdated, refreshTick]);

  // Iter 217 — Periodo orientado a agenda futura.
  // Modos: today | next_7d | next_30d | next_60d | year_remaining | custom
  //        (+ legacy naturales: day/week/month/quarter/year)
  // Default: next_30d. Si el usuario tenía guardado "month" (Iter 210), lo
  // respetamos por persistencia hasta que él mismo cambie.
  // (`customFrom` y `customTo` se declaran arriba para evitar TDZ.)

  const periodRange = useMemo(() => {
    const yr = selectedYear || new Date().getFullYear();
    const now = new Date();
    const todayIso = format(now, "yyyy-MM-dd");
    const fmt = (d) => format(d, "dd/MM/yyyy");
    const labelRange = (start, end) => start === end ? fmt(new Date(start + "T00:00:00")) : `${fmt(new Date(start + "T00:00:00"))} – ${fmt(new Date(end + "T00:00:00"))}`;

    // ── Agenda futura ──
    if (period === "today") {
      return { start: todayIso, end: todayIso, label: `Hoy · ${labelRange(todayIso, todayIso)}` };
    }
    if (period === "next_7d") {
      const end = format(addDays(now, 7), "yyyy-MM-dd");
      return { start: todayIso, end, label: `Próximos 7 días · ${labelRange(todayIso, end)}` };
    }
    if (period === "next_30d") {
      const end = format(addDays(now, 30), "yyyy-MM-dd");
      return { start: todayIso, end, label: `Próximos 30 días · ${labelRange(todayIso, end)}` };
    }
    if (period === "next_60d") {
      const end = format(addDays(now, 60), "yyyy-MM-dd");
      return { start: todayIso, end, label: `Próximos 60 días · ${labelRange(todayIso, end)}` };
    }
    if (period === "year_remaining") {
      const end = format(new Date(now.getFullYear(), 11, 31), "yyyy-MM-dd");
      return { start: todayIso, end, label: `Año restante · ${labelRange(todayIso, end)}` };
    }
    if (period === "custom") {
      if (customFrom && customTo && customFrom <= customTo) {
        return { start: customFrom, end: customTo, label: `Personalizado · ${labelRange(customFrom, customTo)}` };
      }
      // sin fechas: cae a hoy
      return { start: todayIso, end: todayIso, label: "Personalizado · sin fechas" };
    }

    // ── Legacy naturales ──
    const isCurrentYear = yr === now.getFullYear();
    const today = isCurrentYear ? todayIso : `${yr}-01-01`;
    switch (period) {
      case "day": return { start: today, end: today, label: `Día natural · ${labelRange(today, today)}` };
      case "week": {
        const ref = isCurrentYear ? now : new Date(yr, 0, 1);
        const ws = startOfWeek(ref, { weekStartsOn: 1 });
        const we = addDays(ws, 6);
        return { start: format(ws, "yyyy-MM-dd"), end: format(we, "yyyy-MM-dd"), label: `Semana natural · ${labelRange(format(ws, "yyyy-MM-dd"), format(we, "yyyy-MM-dd"))}` };
      }
      case "quarter": {
        const q = isCurrentYear ? Math.floor(now.getMonth() / 3) : 0;
        const qs = new Date(yr, q * 3, 1);
        const qe = new Date(yr, (q + 1) * 3, 0);
        return { start: format(qs, "yyyy-MM-dd"), end: format(qe, "yyyy-MM-dd"), label: `Trimestre natural T${q + 1} ${yr} · ${labelRange(format(qs, "yyyy-MM-dd"), format(qe, "yyyy-MM-dd"))}` };
      }
      case "year": return { start: `${yr}-01-01`, end: `${yr}-12-31`, label: `Año natural ${yr} · ${labelRange(`${yr}-01-01`, `${yr}-12-31`)}` };
      default: { // month
        const ref = isCurrentYear ? now : new Date(yr, 0, 1);
        const ms = startOfMonth(ref);
        const me = endOfMonth(ref);
        return { start: format(ms, "yyyy-MM-dd"), end: format(me, "yyyy-MM-dd"), label: `Mes natural ${format(ref, "MMMM yyyy", { locale: es })} · ${labelRange(format(ms, "yyyy-MM-dd"), format(me, "yyyy-MM-dd"))}` };
      }
    }
  }, [period, selectedYear, customFrom, customTo]);

  // Filter appointments by period
  const periodAppointments = useMemo(() =>
    appointments.filter(a => a.date >= periodRange.start && a.date <= periodRange.end),
    [appointments, periodRange]
  );

  // Iter 219 (P1) — "Confirmadas pasadas" no debe quedar limitada por una
  // ventana FUTURA. Calculamos su propio set según el contexto del periodo.
  // Iter 225 (P1) — FUENTE DE VERDAD: el servidor (`pastConfirmedServer`).
  // Esto garantiza que aunque el array `appointments` esté truncado por el
  // limit del listado general, las confirmadas pasadas se vean todas. La
  // lógica per-periodo vive ahora en el backend (/api/appointments/past-confirmed).
  const todayIso = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  const pastConfirmedAppointments = pastConfirmedServer;

  const handleSave = async () => {
    try {
      if (editingId) {
        await axiosAuth().put(`/appointments/${editingId}`, form);
        toast.success("Cita actualizada");
      } else {
        await axiosAuth().post("/appointments", form);
        toast.success("Cita creada");
      }
      setShowDialog(false);
      resetForm();
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axiosAuth().put(`/appointments/${id}`, { status });
      toast.success("Estado actualizado");
      fetchAppointments();
    } catch {
      toast.error("Error al actualizar estado");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar esta cita?")) return;
    try {
      await axiosAuth().delete(`/appointments/${id}`);
      toast.success("Cita eliminada");
      fetchAppointments();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const openEdit = (apt) => {
    setEditingId(apt.id);
    setForm({
      client_name: apt.client_name || "", client_email: apt.client_email || "",
      client_phone: apt.client_phone || "", vehicle_info: apt.vehicle_info || "",
      service_type: apt.service_type || "", date: apt.date || "",
      time_slot: apt.time_slot || "", notes: apt.notes || "",
      assigned_employee: apt.assigned_employee || ""
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({ client_name: "", client_email: "", client_phone: "", vehicle_info: "", service_type: "", date: "", time_slot: "", notes: "", assigned_employee: "" });
  };

  // filteredAppointments is now computed above with summary card support

  // ── ALERTA "REQUIERE ATENCIÓN" (Iter 131) ────────────────────────────
  // Solo casos REALMENTE accionables, NO canceladas cerradas.
  const isCancelled = (a) => a.status === "cancelada_cliente" || a.status === "cancelada_taller" || a.status === "cancelada";
  const needsCancelReview = (a) => !isCancelled(a) &&
    (a.status === "pendiente_cancelacion" || a.cancellation_requested === true);
  const needsRescheduleReview = (a) => !isCancelled(a) &&
    (a.status === "pendiente_reprogramacion" || a.reschedule_requested === true);
  const needsReplacementReview = (a) => !isCancelled(a) &&
    (a.replacement_vehicle_status === "pending_review" || a.replacement_vehicle_status === "pending_call");
  const isStalePending = (a) => {
    if (isCancelled(a)) return false;
    if (a.status !== "pendiente_confirmacion") return false;
    const t = a.created_at ? new Date(a.created_at).getTime() : 0;
    return t > 0 && (Date.now() - t) > 24 * 3600 * 1000;
  };
  // Iter 222 — Cualquier cita en estado pendiente_confirmacion requiere gestión
  // (no solo las "stale" > 24h). El flag visual ámbar/rojo de antigüedad sigue
  // siendo un indicador independiente.
  const isPendingConfirmation = (a) =>
    !isCancelled(a) && a.status === "pendiente_confirmacion";
  const needsAttention = (a) =>
    needsCancelReview(a) || needsRescheduleReview(a) || needsReplacementReview(a) || isPendingConfirmation(a);

  const attentionApts = useMemo(
    () => periodAppointments.filter(needsAttention),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [periodAppointments]
  );

  // Iter 222 (P1) — "Requieren gestión" NO debe limitarse al periodo de fechas.
  // Si una cita pendiente o que requiere acción cae fuera del rango seleccionado
  // (p.ej. fecha pasada y vemos "Próximos 60 días"), aún debe aparecer en el KPI
  // y en el banner. La fuente de verdad para "trabajo pendiente" es global.
  const pendingAttentionApts = useMemo(
    () => appointments.filter(needsAttention),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appointments]
  );
  const alertCount = pendingAttentionApts.length;
  const attentionReasons = useMemo(() => {
    const r = { cancel: 0, reschedule: 0, replacement: 0, pending: 0 };
    for (const a of pendingAttentionApts) {
      if (needsCancelReview(a)) r.cancel++;
      else if (needsRescheduleReview(a)) r.reschedule++;
      else if (needsReplacementReview(a)) r.replacement++;
      else if (isPendingConfirmation(a)) r.pending++;
    }
    return r;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAttentionApts]);
  const alertMessage = (() => {
    const active = ["cancel", "reschedule", "replacement", "pending"].filter(k => attentionReasons[k] > 0);
    if (active.length === 0) return "";
    if (active.length === 1) {
      if (active[0] === "cancel") return "Hay solicitudes de cancelación pendientes de revisar.";
      if (active[0] === "reschedule") return "Hay solicitudes de reprogramación pendientes.";
      if (active[0] === "replacement") return "Hay solicitudes de vehículo de alquiler pendientes.";
      if (active[0] === "pending") return "Hay citas pendientes de confirmar.";
    }
    return "Hay citas que requieren gestión: pendientes de confirmar, cancelaciones, reprogramaciones o vehículos de alquiler.";
  })();

  // Summary counts for interactive cards (period-filtered)
  // Iter 210: separamos confirmadas en "próximas" vs "pasadas" (estado derivado por
  // fecha/hora en Europe/Madrid). El status real en BD no se modifica.
  // Iter 219: confirmada_pasadas usa pastConfirmedAppointments (no limitado por la
  // ventana futura del periodo). Próximas sigue ligada al rango (ahora→fin).
  // Iter 222: requiere_atencion (KPI "Requieren gestión") es GLOBAL, no se limita
  // al periodo. Una cita pendiente debe aparecer aunque su fecha caiga fuera del rango.
  const counts = {
    total: periodAppointments.length,
    requiere_atencion: pendingAttentionApts.length,
    confirmada: periodAppointments.filter(a => a.status === "confirmada").length,
    confirmada_proximas: periodAppointments.filter(isUpcomingConfirmed).length,
    confirmada_pasadas: pastConfirmedAppointments.length,
    pendiente_reprogramacion: periodAppointments.filter(a => a.status === "pendiente_reprogramacion").length,
    cancelada: periodAppointments.filter(a => a.status === "cancelada_cliente" || a.status === "cancelada_taller").length,
  };

  // Iter 233 (P1) — Contador de citas sin asesor (excluyendo canceladas) dentro
  // del periodo actual. Sirve para el badge de aviso junto al filtro de asesor.
  const unassignedCount = useMemo(() => periodAppointments.filter(
    (a) => !(a.advisor_id || "").trim()
      && a.status !== "cancelada_cliente"
      && a.status !== "cancelada_taller"
  ).length, [periodAppointments]);

  // Asesores activos (no archivados) para el selector
  const activeAdvisors = useMemo(
    () => (advisors || []).filter(a => a.active !== false && !a.archived),
    [advisors]
  );

  // Comparison helpers
  const comp = comparisonData?.comparison || {};
  const getChange = (key) => comp[key]?.change ?? null;

  const summaryCards = [
    { key: "todos", label: "Total", count: counts.total, color: "bg-slate-900 text-white", ring: "ring-slate-300", compKey: "total" },
    // Iter 222: KPI "Requieren gestión" global (sin filtro de periodo).
    { key: "requiere_atencion", label: "Requieren gestión", count: counts.requiere_atencion, color: "bg-amber-50 text-amber-800 border-amber-200", ring: "ring-amber-400", compKey: null },
    // Iter 210: confirmadas próximas (azul fuerte) y pasadas (gris) separadas
    { key: "confirmada_proximas", label: "Confirmadas próximas", count: counts.confirmada_proximas, color: "bg-blue-50 text-blue-800 border-blue-200", ring: "ring-blue-400", compKey: "confirmadas" },
    { key: "confirmada_pasadas", label: "Confirmadas pasadas", count: counts.confirmada_pasadas, color: "bg-slate-100 text-slate-600 border-slate-300", ring: "ring-slate-400", compKey: null },
    { key: "pendiente_reprogramacion", label: "Reprog", count: counts.pendiente_reprogramacion, color: "bg-purple-50 text-purple-800 border-purple-200", ring: "ring-purple-400", compKey: "reprogramadas" },
    { key: "cancelada", label: "Canceladas", count: counts.cancelada, color: "bg-red-50 text-red-800 border-red-200", ring: "ring-red-400", compKey: "canceladas" },
  ];

  // Combined filter: period + status + search
  // Iter 219 — "confirmada_pasadas" usa pastConfirmedAppointments (no limitado
  // por la ventana futura). El resto usa periodAppointments como base.
  // Iter 222 — "requiere_atencion" usa pendingAttentionApts (TODA la BD, sin
  // filtro de periodo). KPI, banner y listado salen del mismo set para que
  // coincidan siempre.
  let _statusBaseSet;
  if (statusFilter === "confirmada_pasadas") _statusBaseSet = pastConfirmedAppointments;
  else if (statusFilter === "requiere_atencion") _statusBaseSet = pendingAttentionApts;
  else _statusBaseSet = periodAppointments;
  const baseFilteredAppointments = _statusBaseSet.filter(a => {
    if (statusFilter !== "todos") {
      if (statusFilter === "requiere_atencion") {
        if (!needsAttention(a)) return false;
      } else if (statusFilter === "cancelada") {
        if (a.status !== "cancelada_cliente" && a.status !== "cancelada_taller") return false;
      } else if (statusFilter === "confirmada_proximas") {
        // Iter 210: solo confirmadas con fecha/hora >= ahora Madrid
        if (!isUpcomingConfirmed(a)) return false;
      } else if (statusFilter === "confirmada_pasadas") {
        // Iter 219: el set base ya está pre-filtrado, doble guarda por si acaso
        if (a?.status !== "confirmada") return false;
        if (aptDateTimeString(a) >= nowInMadrid()) return false;
      } else if (statusFilter === "confirmada") {
        if (a.status !== "confirmada") return false;
        // Sub-filtro temporal cuando el usuario selecciona "Confirmada" desde el Select
        if (confirmadaTemporal === "proximas" && !isUpcomingConfirmed(a)) return false;
        if (confirmadaTemporal === "pasadas" && !isPastConfirmed(a)) return false;
        // "todas" → no aplica subfiltro
      } else if (a.status !== statusFilter) return false;
    }
    // Iter 233 (P1) — Filtro de asesor (compatible con cualquier otro filtro)
    if (advisorFilter !== "todos") {
      const aid = (a.advisor_id || "").trim();
      if (advisorFilter === "unassigned") {
        if (aid) return false;
      } else if (aid !== advisorFilter) {
        return false;
      }
    }
    if (search) {
      const q = search.toLowerCase();
      return (a.client_name || "").toLowerCase().includes(q) ||
        (a.service_type || "").toLowerCase().includes(q) ||
        (a.plate || "").toLowerCase().includes(q) ||
        (a.client_email || "").toLowerCase().includes(q) ||
        (a.client_phone || "").toLowerCase().includes(q);
    }
    return true;
  });

  // Helpers Iter 128: timestamp creación + comparadores de orden
  const getCreatedAt = (a) => {
    const v = a.created_at || a.submitted_at || a.requested_at || a.createdAt;
    if (!v) return 0;
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? t : 0;
  };
  const getApptDateTime = (a) => {
    const dt = `${a.date || "9999-12-31"}T${(a.time_slot || "00:00").slice(0, 5)}:00`;
    const t = new Date(dt).getTime();
    return Number.isFinite(t) ? t : 0;
  };

  const filteredAppointments = useMemo(() => {
    const arr = [...baseFilteredAppointments];
    const cmpStr = (a, b, k) => (a[k] || "").toString().localeCompare((b[k] || "").toString(), "es");
    switch (sortOption) {
      case "appt_date_desc":
        arr.sort((a, b) => getApptDateTime(b) - getApptDateTime(a));
        break;
      case "requested_oldest":
        arr.sort((a, b) => getCreatedAt(a) - getCreatedAt(b));
        break;
      case "requested_newest":
        arr.sort((a, b) => getCreatedAt(b) - getCreatedAt(a));
        break;
      case "status":
        arr.sort((a, b) => cmpStr(a, b, "status") || (getApptDateTime(a) - getApptDateTime(b)));
        break;
      case "client_az":
        arr.sort((a, b) => cmpStr(a, b, "client_name"));
        break;
      case "appt_date_asc":
      default:
        arr.sort((a, b) => getApptDateTime(a) - getApptDateTime(b));
        break;
    }
    return arr;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseFilteredAppointments, sortOption]);

  // Antigüedad humana + flag visual (24h ámbar / 48h rojo) para pendientes
  const ageLabel = (a) => {
    const t = getCreatedAt(a);
    if (!t) return { text: "—", urgency: "none", createdHuman: "" };
    const diffSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
    let text;
    if (diffSec < 60) text = "Solicitada hace unos segundos";
    else if (diffSec < 3600) text = `Solicitada hace ${Math.floor(diffSec / 60)} min`;
    else if (diffSec < 86400) text = `Solicitada hace ${Math.floor(diffSec / 3600)} h`;
    else if (diffSec < 172800) text = "Solicitada ayer";
    else text = `Solicitada hace ${Math.floor(diffSec / 86400)} días`;
    const isPending = a.status === "pendiente_confirmacion" || a.status === "pendiente_reprogramacion";
    let urgency = "none";
    if (isPending) {
      if (diffSec > 48 * 3600) urgency = "red";
      else if (diffSec > 24 * 3600) urgency = "amber";
    }
    const d = new Date(t);
    const pad = (n) => String(n).padStart(2, "0");
    const createdHuman = `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return { text, urgency, createdHuman };
  };

  return (
    <div className="space-y-5" data-testid="appointments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase" data-testid="appointments-title">Gestion de Citas</h1>
          <p className="text-sm text-slate-500 mt-0.5 capitalize">{periodRange.label} &middot; {periodAppointments.length} citas</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Iter 221 — Indicador "X citas cargadas" con aviso si se acerca/alcanza
              el límite de carga (10.000 / 20.000). Discreto en escritorio; se oculta
              en móvil para no romper el header (se reflejará en el subtítulo). */}
          {(() => {
            const loaded = appointments.length;
            const LIMIT = 10000;
            const WARN = 9000;
            const reachedLimit = loaded >= LIMIT;
            const nearLimit = !reachedLimit && loaded >= WARN;
            const baseCls = "hidden sm:inline-flex items-center gap-1.5 px-2 h-9 text-[10px] font-semibold uppercase tracking-wider rounded-sm border";
            let cls = `${baseCls} text-slate-600 bg-slate-50 border-slate-200`;
            let title = `Límite de carga actual: ${LIMIT.toLocaleString("es-ES")} citas`;
            let testid = "loaded-count-indicator";
            let label = `${loaded.toLocaleString("es-ES")} citas cargadas`;
            if (reachedLimit) {
              cls = `${baseCls} text-red-700 bg-red-50 border-red-200`;
              title = `Se alcanzó el límite de ${LIMIT.toLocaleString("es-ES")} citas. Puede haber resultados no incluidos en la vista. Ajusta filtros o contacta con soporte.`;
              testid = "loaded-count-indicator-truncated";
              label = `${loaded.toLocaleString("es-ES")} citas cargadas — posible truncado`;
            } else if (nearLimit) {
              cls = `${baseCls} text-amber-700 bg-amber-50 border-amber-200`;
              title = `Próximo al límite de ${LIMIT.toLocaleString("es-ES")} citas. Si lo superas algunos resultados pueden quedar fuera.`;
              testid = "loaded-count-indicator-warn";
              label = `${loaded.toLocaleString("es-ES")} citas cargadas — cerca del límite`;
            }
            return (
              <span className={cls} title={title} data-testid={testid} aria-label={title}>
                <Database className="w-3.5 h-3.5" />
                <span data-testid="loaded-count-text">{label}</span>
              </span>
            );
          })()}
          {/* Indicador auto-refresh + botón manual */}
          <div className="hidden sm:flex items-center gap-1.5 text-[10px] text-slate-500 mr-1" data-testid="auto-refresh-indicator">
            {refreshError ? (
              <span className="text-amber-700 font-medium" data-testid="auto-refresh-error">
                No se pudo actualizar. Reintentando...
              </span>
            ) : (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                <span>Actualizado <span data-testid="auto-refresh-last-updated">{lastUpdatedLabel}</span></span>
              </>
            )}
          </div>
          <button
            onClick={manualRefresh}
            disabled={isManualRefreshing}
            title="Actualizar ahora"
            data-testid="manual-refresh-btn"
            className="flex items-center gap-1 px-2 h-9 text-xs font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-100 border border-slate-200 rounded-sm disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isManualRefreshing ? "animate-spin" : ""}`} /> Actualizar
          </button>
          <div className="flex border border-slate-200 rounded-sm overflow-hidden">
            <button onClick={() => setActiveView("calendar")} className={`flex items-center gap-1.5 px-3 h-9 text-xs font-semibold uppercase tracking-wider ${activeView === "calendar" ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid="view-calendar-btn">
              <LayoutGrid className="w-3.5 h-3.5" /> Calendario
            </button>
            <button onClick={() => setActiveView("list")} className={`flex items-center gap-1.5 px-3 h-9 text-xs font-semibold uppercase tracking-wider ${activeView === "list" ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid="view-list-btn">
              <List className="w-3.5 h-3.5" /> Lista
            </button>
            <button onClick={() => setActiveView("advisor")} className={`flex items-center gap-1.5 px-3 h-9 text-xs font-semibold uppercase tracking-wider ${activeView === "advisor" ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid="view-advisor-btn">
              <UserCheck className="w-3.5 h-3.5" /> Por Asesor
            </button>
            <button onClick={() => { setActiveView("analytics"); setStatusFilter("todos"); }} className={`flex items-center gap-1.5 px-3 h-9 text-xs font-semibold uppercase tracking-wider ${activeView === "analytics" ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid="view-analytics-btn">
              <BarChart3 className="w-3.5 h-3.5" /> Analitica
            </button>
          </div>
          <Button onClick={() => setShowQuick(true)}
            className="bg-red-600 hover:bg-red-700 text-white rounded-sm text-xs uppercase tracking-wider font-bold h-9 px-4"
            data-testid="quick-reception-btn">
            <Zap className="w-4 h-4 mr-1" /> Recepcion Rapida
          </Button>
          <Button onClick={() => { resetForm(); setShowDialog(true); }}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider font-semibold h-9 px-4"
            data-testid="new-appointment-btn">
            <Plus className="w-4 h-4 mr-1" /> Nueva
          </Button>
          <Button variant="outline" onClick={async () => {
            try {
              const res = await axiosAuth().get("/export/appointments", { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const d = new Date().toISOString().split("T")[0];
              const a = document.createElement("a"); a.href = url; a.download = `citas_ascauto_${d}.xlsx`; a.click();
              toast.success("Excel descargado");
            } catch { toast.error("Error al exportar"); }
          }} className="rounded-sm text-xs uppercase tracking-wider font-semibold h-9 px-3 border-slate-200" data-testid="export-appointments-btn">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {/* Alert banner */}
      {alertCount > 0 && (
        <button
          type="button"
          onClick={() => { setActiveView("list"); setStatusFilter("requiere_atencion"); }}
          className="w-full text-left bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded-sm p-3 flex items-start gap-3 transition-colors"
          data-testid="appointments-alert-banner"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800" data-testid="alert-banner-headline">
              {alertCount} {alertCount === 1 ? "cita requiere gestión" : "citas requieren gestión"}
              <span className="text-amber-700 font-normal"> en {periodRange.label}</span>
            </p>
            <p className="text-xs text-amber-700 mt-0.5" data-testid="alert-banner-message">{alertMessage}</p>
          </div>
          <span className="text-[10px] text-amber-700 underline uppercase tracking-wider self-center flex-shrink-0">
            Ver lista
          </span>
        </button>
      )}

      {/* Period selector */}
      <div className="flex items-center justify-between" data-testid="period-selector-bar">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-slate-400" />
          <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Periodo de analisis:</span>
        </div>
        <div className="flex items-center gap-2">
          <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))}
            className="h-8 px-2 text-xs font-mono font-semibold border border-slate-200 rounded-sm bg-white text-slate-800" data-testid="year-selector">
            {yearOptions.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="period-selector">
          {/* Iter 217 — Agenda futura (orientada a operativa) */}
          {[
            { value: "today", label: "Hoy" },
            { value: "next_7d", label: "Próx. 7d" },
            { value: "next_30d", label: "Próx. 30d" },
            { value: "next_60d", label: "Próx. 60d" },
            { value: "year_remaining", label: "Año rest." },
            { value: "custom", label: "Personalizado" },
          ].map(opt => (
            <button key={opt.value} onClick={() => setPeriod(opt.value)}
              className={`px-2.5 h-8 text-[10px] font-semibold uppercase tracking-wider rounded-sm border transition-colors ${
                period === opt.value ? 'bg-slate-900 text-white border-slate-900' : 'text-slate-600 border-slate-200 hover:bg-slate-100'
              }`}
              data-testid={`period-${opt.value}`}>
              {opt.label}
            </button>
          ))}
          {/* Sub-grupo histórico/natural, separado */}
          <span className="text-[9px] text-slate-300 mx-1">|</span>
          <span className="text-[9px] uppercase tracking-wider text-slate-400 mr-1">Histórico</span>
          {[
            { value: "day", label: "Día nat." },
            { value: "week", label: "Sem. nat." },
            { value: "month", label: "Mes nat." },
            { value: "quarter", label: "Trim. nat." },
            { value: "year", label: "Año nat." },
          ].map(opt => (
            <button key={opt.value} onClick={() => setPeriod(opt.value)}
              className={`px-2.5 h-8 text-[10px] font-semibold uppercase tracking-wider rounded-sm border transition-colors ${
                period === opt.value ? 'bg-slate-700 text-white border-slate-700' : 'text-slate-500 border-slate-200 hover:bg-slate-100'
              }`}
              data-testid={`period-${opt.value}`}>
              {opt.label}
            </button>
          ))}
          </div>
        </div>
        {/* Iter 217 — Rango efectivo siempre visible y selector custom */}
        <div className="flex flex-wrap items-center gap-3 mt-1 text-[11px] text-slate-600" data-testid="period-range-display">
          <span><strong className="text-slate-500 uppercase tracking-wider text-[10px] mr-1">Mostrando:</strong> {periodRange.label}</span>
          {period === "custom" && (
            <span className="flex items-center gap-1.5">
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-7 text-xs border border-slate-200 rounded-sm px-1.5" data-testid="period-custom-from" />
              <span className="text-slate-400">→</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-7 text-xs border border-slate-200 rounded-sm px-1.5" data-testid="period-custom-to" />
            </span>
          )}
        </div>
      </div>

      {/* Interactive Summary Cards with comparisons */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="summary-cards">
        {summaryCards.map(card => {
          const change = card.compKey ? getChange(card.compKey) : null;
          const hasChange = change !== null && change !== 0;
          const isPositive = change > 0;
          const isNegative = change < 0;
          // For "canceladas", UP is BAD (red), DOWN is GOOD (green)
          const isBadMetric = card.compKey === "canceladas" || card.compKey === "reprogramadas";
          const changeColor = hasChange
            ? (isBadMetric ? (isPositive ? "text-red-500" : "text-green-500") : (isPositive ? "text-green-500" : "text-red-500"))
            : "text-slate-400";
          return (
            <button key={card.key} onClick={() => { setStatusFilter(card.key); if (card.key !== "todos") setActiveView("list"); }}
              className={`p-3 rounded-sm border text-left transition-all cursor-pointer hover:shadow-md ${
                statusFilter === card.key ? `${card.color} ring-2 ${card.ring} shadow-sm` : 'bg-white border-slate-200 hover:border-slate-300 hover:scale-[1.02]'
              }`}
              data-testid={`summary-card-${card.key}`}>
              <div className="flex items-end justify-between">
                <p className={`text-2xl font-bold ${statusFilter === card.key && card.key === 'todos' ? 'text-white' : ''}`}>{card.count}</p>
                {hasChange && (
                  <span className={`text-[10px] font-bold flex items-center gap-0.5 ${changeColor}`} data-testid={`card-change-${card.key}`}>
                    {isPositive ? <ArrowUpRight className="w-3 h-3" /> : isNegative ? <ArrowDownRight className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                    {Math.abs(change)}%
                  </span>
                )}
              </div>
              <p className={`text-[10px] uppercase tracking-wider font-semibold mt-0.5 ${
                statusFilter === card.key && card.key === 'todos' ? 'text-slate-300' : 'text-slate-500'
              }`}>{card.label}</p>
            </button>
          );
        })}
      </div>

      {/* Comparison context */}
      {comparisonData?.comparison?.label && (
        <p className="text-[10px] text-slate-400 uppercase tracking-wider text-right -mt-2" data-testid="comparison-label">
          {comparisonData.comparison.label}
        </p>
      )}

      {/* Active filter header */}
      {(statusFilter !== "todos" || search || advisorFilter !== "todos") && (
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2" data-testid="active-filter-header">
          <Filter className="w-3.5 h-3.5 text-slate-500" />
          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider" data-testid="active-filter-label">
            {statusFilter !== "todos" ? (() => {
              // Iter 219 — etiqueta clara y específica para confirmadas pasadas/próximas
              // Iter 222 — etiqueta clara para "Requieren gestión" (sin filtro de periodo).
              const explicit = {
                confirmada_proximas: "citas confirmadas próximas",
                confirmada_pasadas: "citas confirmadas pasadas",
                confirmada: "citas confirmadas",
                pendiente_confirmacion: "citas pendientes",
                pendiente_reprogramacion: "citas pte. reprog.",
                cancelada: "citas canceladas",
                cancelada_cliente: "citas canceladas (cliente)",
                cancelada_taller: "citas canceladas (taller)",
                requiere_atencion: "citas pendientes de gestión",
              }[statusFilter];
              const fallback = (summaryCards.find(c => c.key === statusFilter)?.label || statusFilter).toLowerCase();
              return <>Mostrando: {explicit || `citas ${fallback}`}</>;
            })() : advisorFilter !== "todos" ? (
              <>
                Asesor:{" "}
                {advisorFilter === "unassigned"
                  ? "Sin asignar"
                  : (activeAdvisors.find(a => a.id === advisorFilter)?.name || advisorFilter)}
              </>
            ) : (
              <>Búsqueda activa</>
            )}
          </span>
          <span className="text-xs text-slate-400" data-testid="active-filter-count">({filteredAppointments.length} resultado{filteredAppointments.length !== 1 ? 's' : ''})</span>
          <button
            onClick={() => {
              setStatusFilter("todos");
              setSearch("");
              setAdvisorFilter("todos");
              clearPersistedFiltersByPrefix("appointments.");
              // Iter 210: tras limpiar, persistir explícitamente el default 'proximas'
              // para que localStorage no quede vacío inconsistente con el estado UI.
              setConfirmadaTemporal("proximas");
              toast.success("Filtros limpiados");
            }}
            className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-600 hover:text-slate-900 transition-colors font-semibold"
            data-testid="clear-filters-btn"
          >
            <X className="w-3 h-3" /> Limpiar filtros
          </button>
        </div>
      )}

      {/* ═══ ANALYTICS VIEW ═══ */}
      {activeView === "analytics" && <AnalyticsView period={period} setPeriod={setPeriod} />}

      {/* ═══ CALENDAR VIEW ═══ */}
      {activeView === "calendar" && (
        <CalendarView appointments={appointments} onRefresh={fetchAppointments} config={config} advisors={advisors} />
      )}

      {/* ═══ ADVISOR VIEW ═══ */}
      {activeView === "advisor" && (
        <AdvisorView appointments={appointments} onRefresh={fetchAppointments} config={config} advisors={advisors} />
      )}

      {/* ═══ LIST VIEW ═══ */}
      {activeView === "list" && (
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-[3]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Buscar por nombre, matricula, telefono, email, servicio..." value={search} onChange={(e) => setSearch(e.target.value)}
                className="pl-10 rounded-sm h-9 border-slate-200" data-testid="appointments-search" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-52 rounded-sm h-9 border-slate-200" data-testid="appointments-status-filter">
                <Filter className="w-3 h-3 mr-2" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los estados</SelectItem>
                <SelectItem value="requiere_atencion" data-testid="status-option-requiere-atencion">Requiere atención</SelectItem>
                <SelectItem value="pendiente_confirmacion">Pte. Confirmacion</SelectItem>
                <SelectItem value="confirmada" data-testid="status-option-confirmada">Confirmada</SelectItem>
                <SelectItem value="confirmada_proximas" data-testid="status-option-confirmada-proximas">Confirmadas próximas</SelectItem>
                <SelectItem value="confirmada_pasadas" data-testid="status-option-confirmada-pasadas">Confirmadas pasadas</SelectItem>
                <SelectItem value="pendiente_reprogramacion">Pte. Reprogramacion</SelectItem>
                <SelectItem value="cancelada_cliente">Cancelada (cliente)</SelectItem>
                <SelectItem value="cancelada_taller">Cancelada (taller)</SelectItem>
              </SelectContent>
            </Select>
            {/* Iter 233 (P1) — Filtro de Asesor */}
            <div className="relative">
              <Select value={advisorFilter} onValueChange={setAdvisorFilter}>
                <SelectTrigger className="w-56 rounded-sm h-9 border-slate-200" data-testid="appointments-advisor-filter">
                  <UserCheck className="w-3 h-3 mr-2" /><SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los asesores</SelectItem>
                  <SelectItem value="unassigned" data-testid="advisor-option-unassigned">
                    Sin asesor asignado{unassignedCount > 0 ? ` (${unassignedCount})` : ""}
                  </SelectItem>
                  {activeAdvisors.map((adv) => (
                    <SelectItem key={adv.id} value={adv.id} data-testid={`advisor-option-${adv.id}`}>
                      {adv.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {unassignedCount > 0 && advisorFilter !== "unassigned" && (
                <button
                  type="button"
                  onClick={() => setAdvisorFilter("unassigned")}
                  className="absolute -top-1.5 -right-1.5 bg-orange-500 hover:bg-orange-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm transition-colors uppercase tracking-wide"
                  data-testid="unassigned-badge"
                  title={`${unassignedCount} cita${unassignedCount > 1 ? "s" : ""} sin asesor asignado`}
                >
                  Sin asesor ({unassignedCount})
                </button>
              )}
            </div>
            <Select value={sortOption} onValueChange={(v) => { sortTouchedRef.current = true; setSortOption(v); }}>
              <SelectTrigger className="w-60 rounded-sm h-9 border-slate-200" data-testid="appointments-sort-select">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">Ordenar por</span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="appt_date_asc">Fecha de cita ascendente</SelectItem>
                <SelectItem value="appt_date_desc">Fecha de cita descendente</SelectItem>
                <SelectItem value="requested_oldest">Solicitudes más antiguas primero</SelectItem>
                <SelectItem value="requested_newest">Solicitudes más recientes primero</SelectItem>
                <SelectItem value="status">Estado</SelectItem>
                <SelectItem value="client_az">Cliente A-Z</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Iter 210: sub-toggle temporal cuando filtramos por "Confirmada" */}
          {statusFilter === "confirmada" && (
            <div className="flex items-center gap-2 bg-blue-50/40 border border-blue-100 rounded-sm px-3 py-1.5" data-testid="confirmada-temporal-toggle">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Periodo:</span>
              {[
                { v: "proximas", label: "Próximas" },
                { v: "pasadas", label: "Pasadas" },
                { v: "todas", label: "Todas" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setConfirmadaTemporal(opt.v)}
                  className={`px-2.5 h-7 text-[11px] font-semibold uppercase tracking-wider rounded-sm transition-colors ${confirmadaTemporal === opt.v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 border border-slate-200"}`}
                  data-testid={`confirmada-temporal-${opt.v}`}
                >
                  {opt.label}
                </button>
              ))}
              <span className="text-[10px] text-slate-400 ml-1">
                {confirmadaTemporal === "proximas" && "Solo futuras (no incluye citas con fecha/hora pasada)"}
                {confirmadaTemporal === "pasadas" && "Citas confirmadas cuya hora ya pasó"}
                {confirmadaTemporal === "todas" && "Sin filtrar por fecha/hora"}
              </span>
            </div>
          )}

          {/* Table */}
          <Card className="border-slate-200 rounded-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs uppercase tracking-wider font-semibold">Fecha / Hora</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold hidden md:table-cell">Antigüedad solicitud</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold">Cliente</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold hidden sm:table-cell">Vehiculo</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold">Servicio</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold">Estado</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Cargando...</TableCell></TableRow>
                    ) : filteredAppointments.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">
                        <div className="space-y-1">
                          {advisorFilter !== "todos" ? (
                            <p className="text-sm font-medium" data-testid="empty-advisor-msg">
                              No existen citas para este asesor en el periodo seleccionado.
                            </p>
                          ) : (
                            <p className="text-sm font-medium">No hay citas con este filtro</p>
                          )}
                          {statusFilter !== "todos" && <p className="text-xs">Filtrando por: {summaryCards.find(c => c.key === statusFilter)?.label}</p>}
                          {(statusFilter !== "todos" || search || advisorFilter !== "todos") && (
                            <button onClick={() => { setStatusFilter("todos"); setSearch(""); setAdvisorFilter("todos"); clearPersistedFiltersByPrefix("appointments."); setConfirmadaTemporal("proximas"); }} className="text-xs text-blue-600 hover:underline mt-2" data-testid="empty-clear-filters-btn">
                              Limpiar filtros
                            </button>
                          )}
                        </div>
                      </TableCell></TableRow>
                    ) : (
                      filteredAppointments.map((apt) => {
                        const hasAlert = needsAttention(apt);
                        const age = ageLabel(apt);
                        const urgencyClass = age.urgency === "red"
                          ? "text-red-700 bg-red-50 border-red-200"
                          : age.urgency === "amber"
                            ? "text-amber-800 bg-amber-50 border-amber-200"
                            : "text-slate-600";
                        return (
                          <TableRow key={apt.id} className={`hover:bg-slate-50 ${hasAlert ? 'bg-amber-50/50' : ''}`} data-testid={`appointment-row-${apt.id}`}>
                            <TableCell data-testid={`apt-date-${apt.id}`}>
                              <div className="flex items-center gap-1.5">
                                <Calendar className="w-3 h-3 text-slate-400" />
                                <span className="text-sm font-mono text-slate-700">{(() => { const p = apt.date?.split("-"); return p?.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : apt.date; })()}</span>
                              </div>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <Clock className="w-3 h-3 text-slate-400" />
                                <span className="text-xs font-mono text-slate-500">{apt.time_slot}</span>
                              </div>
                            </TableCell>
                            <TableCell className="hidden md:table-cell" data-testid={`apt-age-${apt.id}`}>
                              <span className={`inline-block text-[11px] px-1.5 py-0.5 rounded-sm border ${age.urgency === "none" ? "border-transparent" : ""} ${urgencyClass}`}>
                                {age.text}
                              </span>
                              {age.createdHuman && (
                                <p className="text-[10px] text-slate-400 mt-0.5">Creada: {age.createdHuman}</p>
                              )}
                            </TableCell>
                            <TableCell>
                              <p className="text-sm font-medium text-slate-900">{apt.client_name}</p>
                              <p className="text-xs text-slate-500">{apt.client_phone}</p>
                              {/* En móvil mostramos también la antigüedad debajo del cliente */}
                              <p className={`md:hidden text-[10px] mt-0.5 ${age.urgency === "red" ? "text-red-700 font-medium" : age.urgency === "amber" ? "text-amber-700 font-medium" : "text-slate-400"}`}>
                                {age.text}
                              </p>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              <p className="text-sm text-slate-700">{apt.brand} {apt.model_name}</p>
                              <p className="text-xs font-mono text-slate-500">{apt.plate}</p>
                            </TableCell>
                            <TableCell className="text-sm text-slate-700">
                              <div data-testid={`apt-service-${apt.id}`}>
                                {/* Iter 223 — Multi-servicio visible.
                                    Estructura: primer servicio en línea principal,
                                    adicionales en sub-línea (compacta) con tooltip si >2. */}
                                {(() => {
                                  const names = (apt.services || [])
                                    .map(s => (typeof s === "string" ? s : (s?.name || "")))
                                    .filter(Boolean);
                                  const primary = names[0] || apt.primary_service || apt.service_type || apt.promo_titulo || "—";
                                  const extras = names.slice(1);
                                  let extrasLine = null;
                                  if (extras.length === 1) {
                                    extrasLine = `+ ${extras[0]}`;
                                  } else if (extras.length === 2) {
                                    extrasLine = `+ ${extras.join(", ")}`;
                                  } else if (extras.length > 2) {
                                    extrasLine = `+ ${extras.length} servicios más`;
                                  }
                                  return (
                                    <>
                                      <div data-testid={`apt-service-primary-${apt.id}`}>{primary}</div>
                                      {extrasLine && (
                                        <div
                                          className="text-[11px] text-slate-500 mt-0.5"
                                          title={extras.length > 2 ? extras.join("\n") : undefined}
                                          data-testid={`apt-service-extras-${apt.id}`}
                                        >
                                          {extrasLine}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                              {(apt.promo_titulo || apt.promo_id || apt.promo?.titulo) && (
                                <span className="mt-0.5 inline-block text-[9px] uppercase tracking-wider font-bold text-amber-700">
                                  Promo: {apt.promo_titulo || apt.promo?.titulo}
                                </span>
                              )}
                              {apt.replacement_vehicle_needed === "yes" && (
                                <span data-testid={`rv-badge-yes-${apt.id}`} className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 border border-amber-300">
                                  <Car className="w-2.5 h-2.5" /> Vehículo de alquiler: Sí
                                </span>
                              )}
                              {apt.replacement_vehicle_needed === "unknown" && (
                                <span data-testid={`rv-badge-unknown-${apt.id}`} className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider font-bold bg-blue-100 text-blue-800 border border-blue-300">
                                  <Car className="w-2.5 h-2.5" /> Vehículo de alquiler: Consultar
                                </span>
                              )}
                              {(!apt.replacement_vehicle_needed || apt.replacement_vehicle_needed === "no") && (
                                <span data-testid={`rv-badge-no-${apt.id}`} className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider text-slate-400 border border-slate-200">
                                  Sin vehículo de alquiler
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <Select value={apt.status} onValueChange={(val) => handleStatusChange(apt.id, val)}>
                                  <SelectTrigger className="h-7 w-44 rounded-sm text-xs border-0 p-0" data-testid={`status-select-${apt.id}`}>
                                    {(() => {
                                      // Iter 210: badge visual diferenciado para confirmadas pasadas
                                      if (isPastConfirmed(apt)) {
                                        return (
                                          <Badge
                                            className="rounded-sm text-[10px] border bg-slate-100 text-slate-600 border-slate-300"
                                            data-testid={`status-badge-confirmada-pasada-${apt.id}`}
                                          >
                                            Confirmada · Pasada
                                          </Badge>
                                        );
                                      }
                                      return (
                                        <Badge
                                          className={`rounded-sm text-[10px] border ${STATUS_MAP[apt.status]?.class || 'bg-slate-100'}`}
                                          data-testid={`status-badge-${apt.status}-${apt.id}`}
                                        >
                                          {STATUS_MAP[apt.status]?.label || apt.status}
                                        </Badge>
                                      );
                                    })()}
                                  </SelectTrigger>
                                  <SelectContent>
                                    {ADMIN_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                {apt.status === "pendiente_reprogramacion" && apt.modification_request && (
                                  <p className="text-[10px] text-purple-700 font-medium flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Modificacion solicitada</p>
                                )}
                                {apt.rescheduled && (
                                  <p data-testid={`rescheduled-badge-${apt.id}`} className="text-[10px] text-amber-700 font-medium flex items-center gap-1">
                                    <RefreshCw className="w-3 h-3" /> Reprogramada
                                  </p>
                                )}
                                {apt.status === "cancelada_cliente" && (
                                  <p className="text-[10px] text-red-700 font-medium flex items-center gap-1"><XCircle className="w-3 h-3" /> Cancelada por cliente</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="sm" onClick={() => setDetailApt(apt)} className="text-xs h-7" data-testid={`view-apt-${apt.id}`}>
                                  <Eye className="w-3 h-3 mr-1" /> Ver
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => openEdit(apt)} className="text-xs h-7" data-testid={`edit-apt-${apt.id}`}>Editar</Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(apt.id)} className="text-xs h-7 text-red-600 hover:text-red-700" data-testid={`delete-apt-${apt.id}`}>
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Detail/Edit Dialog - Full Edit Mode */}
      <Dialog open={!!detailApt} onOpenChange={() => { setDetailApt(null); fetchAppointments(); }}>
        <DialogContent className="max-w-3xl rounded-sm max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="appointment-detail-dialog">
          <DialogHeader><DialogTitle className="heading-industrial text-lg uppercase">Detalle de Cita</DialogTitle></DialogHeader>
          {detailApt && <DetailEditForm apt={detailApt} config={config} onClose={() => { setDetailApt(null); fetchAppointments(); }} onSave={async (updated) => {
            try {
              // Iter 152 — usar respuesta del PUT (ya incluye `updated_at` nuevo) para
              // que el modal re-fetche historial sin cerrarse. Devolvemos la promise
              // para que DetailEditForm pueda encadenar refetchHistory().
              const r = await axiosAuth().put(`/appointments/${detailApt.id}`, updated);
              toast.success("Cita actualizada. Se notificara al cliente si aplica.");
              if (r?.data) setDetailApt(r.data);
              fetchAppointments();
            } catch (err) { toast.error(err.response?.data?.detail || "Error"); throw err; }
          }} axiosAuth={axiosAuth} />}
        </DialogContent>
      </Dialog>

      {/* Create/Quick Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="appointment-dialog">
          <DialogHeader><DialogTitle className="heading-industrial text-lg uppercase">{editingId ? "Editar Cita" : "Nueva Cita"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Nombre *</Label>
                <Input value={form.client_name} onChange={(e) => setForm({...form, client_name: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="dialog-client-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Telefono</Label>
                <Input value={form.client_phone} onChange={(e) => setForm({...form, client_phone: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="dialog-client-phone" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email</Label>
              <Input value={form.client_email} onChange={(e) => setForm({...form, client_email: e.target.value})} placeholder="email@cliente.com" className="rounded-sm h-9 border-slate-200" data-testid="dialog-client-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Fecha *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="dialog-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Hora *</Label>
                <Input value={form.time_slot} onChange={(e) => setForm({...form, time_slot: e.target.value})} placeholder="09:00" className="rounded-sm h-9 border-slate-200" data-testid="dialog-time" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Servicio *</Label>
              <Input value={form.service_type} onChange={(e) => setForm({...form, service_type: e.target.value})} placeholder="Tipo de servicio" className="rounded-sm h-9 border-slate-200" data-testid="dialog-service" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Vehiculo</Label>
              <Input value={form.vehicle_info} onChange={(e) => setForm({...form, vehicle_info: e.target.value})} placeholder="Marca, modelo, matricula" className="rounded-sm h-9 border-slate-200" data-testid="dialog-vehicle" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-sm text-xs uppercase" data-testid="dialog-cancel">Cancelar</Button>
            <Button onClick={handleSave} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold" data-testid="dialog-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ QUICK RECEPTION DIALOG ═══ */}
      <Dialog open={showQuick} onOpenChange={setShowQuick}>
        <DialogContent className="max-w-4xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="quick-reception-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase flex items-center gap-2">
              <Zap className="w-5 h-5 text-red-600" /> Recepcion Rapida
            </DialogTitle>
          </DialogHeader>
          <QuickReception onClose={() => setShowQuick(false)} onCreated={fetchAppointments} config={config} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
